import { Controller } from "@hotwired/stimulus"

/**
 * Debounced live search controller for surgeon directory
 *
 * Usage:
 * <div data-controller="search" data-search-url-value="/surgeons">
 *   <input type="text" data-search-target="input" data-action="input->search#perform">
 *   <div data-search-target="results"></div>
 * </div>
 */
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    url: String,
    delay: { type: Number, default: 300 }
  }

  connect() {
    this.timeout = null
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  perform() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.search()
    }, this.delayValue)
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (query.length === 0) {
      this.clearResults()
      return
    }

    const url = this.buildUrl(query)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Abort any in-flight request so a slow response can't clobber a newer one.
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    fetch(url, {
      signal: this.abortController.signal,
      headers: {
        "Accept": "text/vnd.turbo-stream.html, text/html, application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("text/vnd.turbo-stream.html")) {
        return response.text().then(html => {
          Turbo.renderStreamMessage(html)
        })
      } else if (contentType && contentType.includes("text/html")) {
        return response.text().then(html => {
          if (this.hasResultsTarget) {
            // For HTML responses from the server, we trust the server-rendered content
            this.resultsTarget.replaceChildren()
            const template = document.createElement("template")
            template.innerHTML = html
            this.resultsTarget.appendChild(template.content)
          }
        })
      } else {
        return response.json().then(data => {
          this.renderResults(data)
        })
      }
    })
    .catch(error => {
      if (error.name === "AbortError") return
      console.error("Search error:", error)
      this.showError()
    })
  }

  buildUrl(query) {
    const baseUrl = this.urlValue || window.location.pathname
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("search", query)
    return url.toString()
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.replaceChildren()
    }
  }

  showError() {
    if (!this.hasResultsTarget) return

    this.resultsTarget.replaceChildren()
    const errorMessage = document.createElement("p")
    errorMessage.className = "text-red-500 p-4 text-center"
    errorMessage.textContent = "Search failed. Please try again."
    this.resultsTarget.appendChild(errorMessage)
  }

  renderResults(data) {
    if (!this.hasResultsTarget || !data.surgeons) return

    this.resultsTarget.replaceChildren()

    if (data.surgeons.length === 0) {
      const noResults = document.createElement("p")
      noResults.className = "text-slate-500 p-4"
      noResults.textContent = "No results found"
      this.resultsTarget.appendChild(noResults)
      return
    }

    data.surgeons.forEach(surgeon => {
      const link = document.createElement("a")
      link.href = `/surgeons/${surgeon.id}`
      link.className = "block p-4 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"

      const name = document.createElement("div")
      name.className = "font-medium text-slate-800"
      name.textContent = surgeon.name || ""

      const practice = document.createElement("div")
      practice.className = "text-sm text-slate-500"
      practice.textContent = surgeon.practice || ""

      const location = document.createElement("div")
      location.className = "text-xs text-slate-400 mt-1"
      location.textContent = `${surgeon.city || ""}, ${surgeon.state || ""}`

      link.appendChild(name)
      link.appendChild(practice)
      link.appendChild(location)
      this.resultsTarget.appendChild(link)
    })
  }
}
